import { type FC, useMemo } from 'react';

interface WalletSparklineProps {
	series: number[];
	width?: number;
	height?: number;
	accent?: 'positive' | 'negative' | 'neutral';
	emptyHint?: string;
}

const ACCENT_TO_VAR: Record<NonNullable<WalletSparklineProps['accent']>, string> = {
	positive: 'var(--color-success, #2E8B57)',
	negative: 'var(--color-danger, #C53030)',
	neutral: 'var(--color-primary, #1B6B4A)',
};

const WalletSparkline: FC<WalletSparklineProps> = ({
	series,
	width = 220,
	height = 44,
	accent = 'neutral',
	emptyHint = 'No activity yet',
}) => {
	const { points, areaPoints, lastDotX, lastDotY, isFlat } = useMemo(() => {
		if (series.length < 2) {
			return { points: '', areaPoints: '', lastDotX: 0, lastDotY: 0, isFlat: true };
		}
		const min = Math.min(...series);
		const max = Math.max(...series);
		const range = max - min || 1;
		const stepX = width / (series.length - 1);
		const padY = 4;
		const usableHeight = height - padY * 2;
		const coords = series.map((v, i) => {
			const x = i * stepX;
			const y = padY + (1 - (v - min) / range) * usableHeight;
			return { x, y };
		});
		const linePath = coords.map((c) => `${c.x.toFixed(2)},${c.y.toFixed(2)}`).join(' ');
		const areaPath = `0,${height} ${linePath} ${width},${height}`;
		const last = coords[coords.length - 1];
		return {
			points: linePath,
			areaPoints: areaPath,
			lastDotX: last.x,
			lastDotY: last.y,
			isFlat: max === min,
		};
	}, [series, width, height]);

	if (series.length < 2) {
		return (
			<div
				className='wallet-sparkline wallet-sparkline--empty'
				style={{ width, height }}
				role='img'
				aria-label={emptyHint}
			>
				<span className='wallet-sparkline__empty-line' />
				<span className='wallet-sparkline__empty-text'>{emptyHint}</span>
			</div>
		);
	}

	const stroke = ACCENT_TO_VAR[accent];

	return (
		<svg
			className='wallet-sparkline'
			width={width}
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			role='img'
			aria-label='Balance trend'
		>
			<defs>
				<linearGradient
					id={`wallet-sparkline-fill-${accent}`}
					x1='0'
					x2='0'
					y1='0'
					y2='1'
				>
					<stop
						offset='0%'
						stopColor={stroke}
						stopOpacity='0.18'
					/>
					<stop
						offset='100%'
						stopColor={stroke}
						stopOpacity='0'
					/>
				</linearGradient>
			</defs>
			<polygon
				points={areaPoints}
				fill={`url(#wallet-sparkline-fill-${accent})`}
			/>
			<polyline
				points={points}
				fill='none'
				stroke={stroke}
				strokeWidth={isFlat ? 1.25 : 1.6}
				strokeLinecap='round'
				strokeLinejoin='round'
			/>
			<circle
				cx={lastDotX}
				cy={lastDotY}
				r={2.6}
				fill={stroke}
			/>
		</svg>
	);
};

export default WalletSparkline;
